/**
 * Chinese Data Service - Unified Data Layer
 * 
 * Provides a unified interface for Chinese learning data with feature flags.
 * Supports gradual migration from Supabase to A2A backend.
 * 
 * Feature Flags:
 * - USE_A2A_BACKEND: Enable A2A backend (default: true)
 * - FALLBACK_TO_SUPABASE: Fallback to Supabase on A2A failure (default: true)
 */

import a2aChineseService from '../api/services/a2aChineseService';

// Import existing Supabase services (if available)
let supabaseKnowledgeGraphService = null;
let supabaseClient = null;

try {
  // Try to import existing Supabase services
  // The module uses ES6 exports, so we need to handle both default and named exports
  const supabaseModule = require('./supabase/knowledgeGraphService');
  
  // Check if it's a default export or named exports
  if (supabaseModule.default) {
    // Default export case
    const defaultExport = supabaseModule.default;
    if (defaultExport.getMockDataByStoryId) {
      supabaseKnowledgeGraphService = defaultExport;
    }
  } else if (supabaseModule.getMockDataByStoryId) {
    // Named exports case - create a service object
    supabaseKnowledgeGraphService = {
      getMockDataByStoryId: supabaseModule.getMockDataByStoryId,
      getAllSampleTexts: supabaseModule.getAllSampleTexts,
      saveMockData: supabaseModule.saveMockData
    };
  }
} catch (error) {
  console.warn('[ChineseDataService] Supabase knowledge graph service not found:', error.message);
}

try {
  const supabaseClientModule = require('./supabaseClient');
  supabaseClient = supabaseClientModule.supabase || supabaseClientModule.default;
} catch (error) {
  console.warn('[ChineseDataService] Supabase client not found:', error.message);
}

/**
 * Feature Flags Configuration
 * Can be overridden via environment variables
 */
const FeatureFlags = {
  USE_A2A_BACKEND: process.env.REACT_APP_USE_A2A_BACKEND !== 'false', // Default: true
  FALLBACK_TO_SUPABASE: process.env.REACT_APP_FALLBACK_TO_SUPABASE !== 'false', // Default: true
  ENABLE_LOGGING: process.env.NODE_ENV === 'development',
};

/**
 * Chinese Data Service Class
 */
class ChineseDataService {
  constructor() {
    this.useA2A = FeatureFlags.USE_A2A_BACKEND;
    this.fallbackToSupabase = FeatureFlags.FALLBACK_TO_SUPABASE;
    this.enableLogging = FeatureFlags.ENABLE_LOGGING;
    
    // Cache for capability checks to avoid repeated calls
    this.capabilityCache = new Map();
    
    this.log('ChineseDataService initialized', {
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase
    });
  }

  /**
   * Internal logging method
   */
  log(message, data = null) {
    if (this.enableLogging) {
      console.log(`[ChineseDataService] ${message}`, data || '');
    }
  }

  /**
   * Internal error logging method
   * Always logs errors (even in production) for debugging
   */
  logError(message, error) {
    // Always log errors, even in production, for debugging A2A issues
    console.error(`[ChineseDataService] ${message}`, error);
    // Also log error details for production debugging
    if (error && typeof error === 'object') {
      console.error(`[ChineseDataService] Error details:`, {
        message: error.message,
        name: error.name,
        stack: error.stack,
        response: error.response,
        status: error.status,
        code: error.code
      });
    }
  }

  /**
   * Try A2A with optional Supabase fallback
   */
  async tryWithFallback(a2aMethod, supabaseFallback, methodName) {
    // Try A2A first if enabled
    if (this.useA2A) {
      try {
        this.log(`${methodName}: Using A2A backend`);
        const result = await a2aMethod();
        this.log(`${methodName}: A2A success`);
        return result;
      } catch (error) {
        // Always log A2A errors in production for debugging
        console.error(`[ChineseDataService] ${methodName}: A2A call failed`, {
          error: error.message,
          name: error.name,
          stack: error.stack,
          response: error.response,
          status: error.status,
          code: error.code
        });
        
        // Check if this is a connection error or backend configuration error
        const isConnectionError = error.message && (
          error.message.includes('Failed to fetch') ||
          error.message.includes('ERR_CONNECTION_REFUSED') ||
          error.message.includes('NETWORK_ERROR') ||
          error.message.includes('Invalid URL string') ||
          error.message.includes('Invalid URL') ||
          error.message.includes('CORS') ||
          error.message.includes('NetworkError')
        );
        
        // Only log errors if not a connection/backend error, or if fallback is not available
        const fallbackAvailable = this.fallbackToSupabase && supabaseFallback;
        if (!isConnectionError || !fallbackAvailable) {
          this.logError(`${methodName}: A2A failed`, error);
        } else {
          // Connection/backend error with fallback available - log warning
          console.warn(`[ChineseDataService] ${methodName}: A2A backend error (${error.message}), using Supabase fallback`);
        }
        
        // Fallback to Supabase if enabled
        if (fallbackAvailable) {
          console.log(`[ChineseDataService] ${methodName}: Falling back to Supabase`);
          try {
            const result = await supabaseFallback();
            console.log(`[ChineseDataService] ${methodName}: Supabase fallback success`);
            return result;
          } catch (supabaseError) {
            this.logError(`${methodName}: Supabase fallback failed`, supabaseError);
            throw supabaseError;
          }
        }
        
        throw error;
      }
    }
    
    // Use Supabase directly if A2A is disabled
    if (supabaseFallback) {
      this.log(`${methodName}: A2A disabled, using Supabase`);
      return await supabaseFallback();
    }
    
    throw new Error(`${methodName}: A2A disabled and no Supabase fallback available`);
  }

  // ============================================================================
  // Knowledge Graph Methods
  // ============================================================================

  /**
   * Get mock data by story ID (for backward compatibility)
   * @param {string} storyId - Story ID
   * @returns {Promise<Object>} Mock data
   */
  async getMockDataByStoryId(storyId) {
    // Validate storyId
    if (!storyId || typeof storyId !== 'string') {
      this.logError('getMockDataByStoryId: Invalid storyId', { storyId, type: typeof storyId });
      throw new Error(`Invalid storyId: ${storyId}`);
    }

    return await this.tryWithFallback(
      // A2A: Get specific analysis
      async () => {
        const result = await a2aChineseService.getKnowledgeGraphAnalysis(storyId);
        this.log('getMockDataByStoryId: A2A result structure', {
          hasResult: !!result,
          hasAnalysis: !!(result && result.analysis),
          resultKeys: result ? Object.keys(result) : [],
          success: result && result.success,
          storyId: storyId
        });
        
        // Handle case where backend returns success: false
        if (result && result.success === false) {
          const errorMessage = result.error || 'Failed to fetch analysis';
          this.log('getMockDataByStoryId: Backend returned success: false', errorMessage);
          // Throw error to trigger fallback, but don't log as critical error if fallback is available
          const fallbackAvailable = this.fallbackToSupabase && supabaseKnowledgeGraphService && supabaseKnowledgeGraphService.getMockDataByStoryId;
          if (!fallbackAvailable) {
            this.logError('getMockDataByStoryId: A2A failed and no fallback available', errorMessage);
          }
          throw new Error(errorMessage);
        }
        
        const analysis = result && result.analysis ? result.analysis : result;
        
        if (!analysis) {
          throw new Error(`Analysis not found for storyId: ${storyId}`);
        }
        
        // Transform A2A format to match component expected format
        // A2A returns: { graph: { nodes: [...], links: [...] }, ... }
        // Component expects: { nodes: [...], links: [...] }
        const transformedData = {
          // Extract nodes and links from graph structure
          nodes: analysis.graph?.nodes || analysis.nodes || [],
          links: analysis.graph?.links || analysis.links || [],
          // Preserve other fields
          summary: analysis.summary || {},
          totalEntities: analysis.total_entities || (analysis.graph?.nodes ? analysis.graph.nodes.length : 0),
          totalRelationships: analysis.total_relationships || (analysis.graph?.links ? analysis.graph.links.length : 0),
          mainThemes: analysis.main_themes || [],
          keyInsights: analysis.key_insights || '',
          // Preserve metadata
          id: analysis.id || analysis.sample_id,
          sample_id: analysis.sample_id || analysis.id,
          sample_name: analysis.sample_name,
          sample_text: analysis.sample_text
        };
        
        this.log('getMockDataByStoryId: Extracted and transformed analysis', {
          hasNodes: !!(transformedData.nodes && transformedData.nodes.length > 0),
          hasLinks: !!(transformedData.links && transformedData.links.length > 0),
          nodesCount: transformedData.nodes.length,
          linksCount: transformedData.links.length,
          originalHasGraph: !!(analysis.graph),
          originalHasDirectNodes: !!(analysis.nodes)
        });
        
        return transformedData;
      },
      // Supabase fallback
      supabaseKnowledgeGraphService && supabaseKnowledgeGraphService.getMockDataByStoryId
        ? async () => {
            this.log('getMockDataByStoryId: Using Supabase fallback', { storyId });
            try {
              const result = await supabaseKnowledgeGraphService.getMockDataByStoryId(storyId);
              
              // Enhanced logging
              console.log('[ChineseDataService] Supabase fallback result:', {
                storyId,
                hasResult: !!result,
                resultType: typeof result,
                hasNodes: result && result.nodes,
                hasLinks: result && result.links,
                nodesCount: result && result.nodes ? result.nodes.length : 0,
                linksCount: result && result.links ? result.links.length : 0,
                resultKeys: result ? Object.keys(result) : []
              });
              
              if (result) {
                this.log('getMockDataByStoryId: Supabase fallback success', {
                  hasNodes: !!(result.nodes),
                  hasLinks: !!(result.links),
                  nodesCount: result.nodes ? result.nodes.length : 0,
                  linksCount: result.links ? result.links.length : 0
                });
              } else {
                console.warn(`[ChineseDataService] No data found in Supabase for storyId: ${storyId}`);
              }
              return result;
            } catch (supabaseError) {
              this.logError('getMockDataByStoryId: Supabase fallback failed', supabaseError);
              throw supabaseError;
            }
          }
        : null,
      'getMockDataByStoryId'
    );
  }

  /**
   * Get all sample texts
   * @returns {Promise<Array>} Sample texts
   */
  async getAllSampleTexts() {
    return await this.tryWithFallback(
      // A2A: Get all analyses and transform to expected format
      async () => {
        const result = await a2aChineseService.getKnowledgeGraphAnalyses(null, 1, 100);
        this.log('getAllSampleTexts: A2A result structure', {
          hasResult: !!result,
          hasAnalyses: !!(result && result.analyses),
          resultKeys: result ? Object.keys(result) : [],
          analysesCount: result && result.analyses ? result.analyses.length : 0,
          success: result && result.success
        });
        
        // Handle case where backend returns success: false
        if (result && result.success === false) {
          this.log('getAllSampleTexts: Backend returned success: false', result.error);
          throw new Error(result.error || 'Failed to fetch analyses');
        }
        
        const analyses = result && result.analyses ? result.analyses : (Array.isArray(result) ? result : []);
        
        this.log('getAllSampleTexts: Extracted analyses', {
          count: analyses.length,
          sample: analyses.length > 0 ? {
            id: analyses[0].id || analyses[0].sample_id,
            name: analyses[0].sample_name || analyses[0].sampleName
          } : null
        });
        
        // Transform A2A format to match Supabase format: { sampleList: [], sampleTexts: {} }
        const sampleList = analyses.map(analysis => ({
          id: analysis.id || analysis.sample_id,
          name: analysis.sample_name || analysis.sampleName || 'Unknown'
        }));
        
        const sampleTexts = {};
        analyses.forEach(analysis => {
          const id = analysis.id || analysis.sample_id;
          sampleTexts[id] = analysis.sample_text || analysis.sampleText || '';
        });
        
        this.log('getAllSampleTexts: Transformed result', {
          sampleListCount: sampleList.length,
          sampleTextsCount: Object.keys(sampleTexts).length
        });
        
        return { sampleList, sampleTexts };
      },
      // Supabase fallback
      supabaseKnowledgeGraphService && supabaseKnowledgeGraphService.getAllSampleTexts
        ? async () => {
            this.log('getAllSampleTexts: Using Supabase fallback');
            try {
              const result = await supabaseKnowledgeGraphService.getAllSampleTexts();
              
              // Enhanced logging
              console.log('[ChineseDataService] Supabase getAllSampleTexts result:', {
                hasResult: !!result,
                hasSampleList: !!(result && result.sampleList),
                hasSampleTexts: !!(result && result.sampleTexts),
                sampleListCount: result && result.sampleList ? result.sampleList.length : 0,
                sampleTextsCount: result && result.sampleTexts ? Object.keys(result.sampleTexts).length : 0,
                sampleIds: result && result.sampleList ? result.sampleList.map(s => s.id) : []
              });
              
              if (result) {
                this.log('getAllSampleTexts: Supabase fallback success', {
                  sampleListCount: result.sampleList ? result.sampleList.length : 0,
                  sampleTextsCount: result.sampleTexts ? Object.keys(result.sampleTexts).length : 0
                });
              }
              return result;
            } catch (supabaseError) {
              this.logError('getAllSampleTexts: Supabase fallback failed', supabaseError);
              throw supabaseError;
            }
          }
        : null,
      'getAllSampleTexts'
    );
  }

  /**
   * Analyze knowledge graph (A2A only - new feature)
   * @param {string} text - Chinese text to analyze
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} Knowledge graph analysis
   */
  async analyzeKnowledgeGraph(text, options = {}) {
    if (!this.useA2A) {
      throw new Error('Knowledge graph analysis requires A2A backend');
    }

    this.log('analyzeKnowledgeGraph: Using A2A (no fallback)');
    const result = await a2aChineseService.analyzeKnowledgeGraph(text, options);
    return result;
  }

  /**
   * Save knowledge graph analysis
   * @param {string} sampleName - Sample name
   * @param {string} sampleText - Sample text
   * @param {Object} graphData - Graph data
   * @param {Object} summary - Summary
   * @returns {Promise<Object>} Saved analysis
   */
  async saveKnowledgeGraphAnalysis(sampleName, sampleText, graphData, summary = null) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.saveKnowledgeGraphAnalysis(
          sampleName,
          sampleText,
          graphData,
          summary
        );
      },
      // Supabase fallback
      supabaseKnowledgeGraphService
        ? async () => {
            return await supabaseKnowledgeGraphService.saveMockData({
              sample_name: sampleName,
              sample_text: sampleText,
              graph_nodes: graphData.nodes,
              graph_links: graphData.links,
              graph_summary: summary || {}
            });
          }
        : null,
      'saveKnowledgeGraphAnalysis'
    );
  }

  // ============================================================================
  // Vocabulary Methods
  // ============================================================================

  /**
   * Get vocabulary sets (A2A only)
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Vocabulary sets
   */
  async getVocabularySets(options = {}) {
    if (!this.useA2A) {
      throw new Error('Vocabulary sets feature requires A2A backend');
    }

    this.log('getVocabularySets: Using A2A (no fallback)');
    return await a2aChineseService.getVocabularySets(options);
  }

  /**
   * Get vocabulary set details
   * @param {string} setId - Set ID
   * @returns {Promise<Object>} Vocabulary set
   */
  async getVocabularySet(setId) {
    if (!this.useA2A) {
      throw new Error('Vocabulary set details require A2A backend');
    }

    this.log('getVocabularySet: Using A2A (no fallback)');
    return await a2aChineseService.getVocabularySet(setId);
  }

  /**
   * Update vocabulary progress
   * @param {string} userId - User ID
   * @param {string} setId - Set ID
   * @param {string} phraseId - Phrase ID
   * @param {string} status - Status
   * @param {number} score - Score
   * @returns {Promise<Object>} Updated progress
   */
  async updateVocabularyProgress(userId, setId, phraseId, status, score = null) {
    if (!this.useA2A) {
      throw new Error('Vocabulary progress requires A2A backend');
    }

    this.log('updateVocabularyProgress: Using A2A (no fallback)');
    return await a2aChineseService.updateProgress(userId, setId, phraseId, status, score);
  }

  /**
   * Save vocabulary test result
   * @param {string} userId - User ID
   * @param {string} setId - Set ID
   * @param {number} score - Score
   * @param {number} totalQuestions - Total questions
   * @param {Array} answers - Answers
   * @returns {Promise<Object>} Test result
   */
  async saveVocabularyTestResult(userId, setId, score, totalQuestions, answers) {
    if (!this.useA2A) {
      throw new Error('Vocabulary test results require A2A backend');
    }

    this.log('saveVocabularyTestResult: Using A2A (no fallback)');
    return await a2aChineseService.saveTestResult(userId, setId, score, totalQuestions, answers);
  }

  // ============================================================================
  // Writing Tools Methods
  // ============================================================================

  /**
   * Save composition
   * @param {Object} compositionData - Composition data
   * @returns {Promise<Object>} Saved composition
   */
  async saveComposition(compositionData) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.saveComposition(compositionData);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            const { data, error } = await supabaseClient
              .from('chi_writingtools_composition')
              .insert({
                user_id: compositionData.userId,
                title: compositionData.title,
                final_composition_text: compositionData.content,
                style: compositionData.style || 'composition',
                word_length: compositionData.wordLength || 0,
                nodes_data: compositionData.nodesData || [],
                category: compositionData.category || 'AIComposition',
                is_active: true
              })
              .select()
              .single();

            if (error) throw error;
            return { success: true, composition: data };
          }
        : null,
      'saveComposition'
    );
  }

  /**
   * Fetch compositions
   * @param {string} userId - User ID
   * @param {string} category - Category
   * @param {number} page - Page
   * @param {number} limit - Limit
   * @returns {Promise<Object>} Compositions
   */
  async fetchCompositions(userId = null, category = null, page = 1, limit = 20) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.getCompositions(userId, category, page, limit);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            let query = supabaseClient
              .from('chi_writingtools_composition')
              .select('*')
              .eq('is_active', true);

            if (userId) query = query.eq('user_id', userId);
            if (category) query = query.eq('category', category);

            const offset = (page - 1) * limit;
            query = query.range(offset, offset + limit - 1);

            const { data, error } = await query;
            if (error) throw error;

            return {
              success: true,
              compositions: data,
              pagination: { page, limit, total: data.length }
            };
          }
        : null,
      'fetchCompositions'
    );
  }

  /**
   * Get composition by ID
   * @param {string} compositionId - Composition ID
   * @returns {Promise<Object>} Composition
   */
  async getComposition(compositionId) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.getComposition(compositionId);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            const { data, error } = await supabaseClient
              .from('chi_writingtools_composition')
              .select('*')
              .eq('id', compositionId)
              .single();

            if (error) throw error;
            return { success: true, composition: data };
          }
        : null,
      'getComposition'
    );
  }

  /**
   * Update composition
   * @param {string} compositionId - Composition ID
   * @param {Object} updates - Updates
   * @returns {Promise<Object>} Updated composition
   */
  async updateComposition(compositionId, updates) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.updateComposition(compositionId, updates);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            const updateData = {};
            if (updates.title) updateData.title = updates.title;
            if (updates.content) updateData.final_composition_text = updates.content;
            if (updates.nodesData !== undefined) updateData.nodes_data = updates.nodesData;

            const { data, error } = await supabaseClient
              .from('chi_writingtools_composition')
              .update(updateData)
              .eq('id', compositionId)
              .select()
              .single();

            if (error) throw error;
            return { success: true, composition: data };
          }
        : null,
      'updateComposition'
    );
  }

  /**
   * Delete composition
   * @param {string} compositionId - Composition ID
   * @returns {Promise<Object>} Deletion status
   */
  async deleteComposition(compositionId) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.deleteComposition(compositionId);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            const { error } = await supabaseClient
              .from('chi_writingtools_composition')
              .update({ is_active: false })
              .eq('id', compositionId);

            if (error) throw error;
            return { success: true, deleted: true };
          }
        : null,
      'deleteComposition'
    );
  }

  // ============================================================================
  // DeepSeek AI Methods (A2A only - new features)
  // ============================================================================

  /**
   * Generate essay (A2A only - new feature)
   * @param {Object} essayConfig - Essay configuration
   * @returns {Promise<Object>} Generated essay
   */
  async generateEssay(essayConfig) {
    if (!this.useA2A) {
      throw new Error('Essay generation requires A2A backend with DeepSeek AI');
    }

    this.log('generateEssay: Using A2A (no fallback)');
    return await a2aChineseService.generateEssay(essayConfig);
  }

  /**
   * Expand sentence (A2A only - new feature)
   * @param {string} keyword - Keyword
   * @param {Object} options - Options
   * @returns {Promise<Object>} Expanded sentence
   */
  async expandSentence(keyword, options = {}) {
    if (!this.useA2A) {
      throw new Error('Sentence expansion requires A2A backend with DeepSeek AI');
    }

    this.log('expandSentence: Using A2A (no fallback)');
    return await a2aChineseService.expandSentence(keyword, options);
  }

  /**
   * Generate drill questions (A2A only - new feature)
   * @param {Object} drillConfig - Drill configuration
   * @returns {Promise<Object>} Generated drills
   */
  async generateDrillQuestions(drillConfig) {
    if (!this.useA2A) {
      throw new Error('Drill generation requires A2A backend with DeepSeek AI');
    }

    this.log('generateDrillQuestions: Using A2A (no fallback)');
    return await a2aChineseService.generateDrillQuestions(drillConfig);
  }

  // ============================================================================
  // UX Negotiation Methods
  // ============================================================================

  /**
   * Negotiate UX format
   * @param {Object} context - User context
   * @returns {Promise<Object>} UX format recommendations
   */
  async negotiateUX(context = null) {
    if (!this.useA2A) {
      this.log('negotiateUX: A2A disabled, returning default UX');
      return this.getDefaultUXFormat(context);
    }

    try {
      this.log('negotiateUX: Using A2A backend');
      return await a2aChineseService.negotiateUX(context);
    } catch (error) {
      this.logError('negotiateUX: A2A failed, returning default', error);
      return this.getDefaultUXFormat(context);
    }
  }

  /**
   * Get default UX format
   * @param {Object} context - User context
   * @returns {Object} Default UX format
   */
  getDefaultUXFormat(context) {
    const detectedContext = context || a2aChineseService.detectContext();
    
    const isMobile = detectedContext.device === 'mobile' || detectedContext.screen_width < 768;
    const isSlowNetwork = ['2g', '3g'].includes(detectedContext.bandwidth);

    return {
      layout: isMobile ? 'cards' : 'table',
      data_granularity: isSlowNetwork ? 'summary' : 'detailed',
      max_items_per_page: isMobile ? 5 : 10,
      include_help: detectedContext.user_level === 'beginner',
      enable_lazy_loading: isSlowNetwork || isMobile,
      font_size: isMobile ? 'large' : 'medium',
      load_images: !isSlowNetwork,
      character_set: detectedContext.preferred_language || 'traditional',
      show_pinyin: detectedContext.user_level === 'beginner',
      language: (detectedContext.preferred_language || 'traditional') === 'traditional' ? 'zh-HK' : 'zh-CN',
    };
  }

  /**
   * Detect user context
   * @returns {Object} User context
   */
  detectContext() {
    return a2aChineseService.detectContext();
  }

  // ============================================================================
  // HKDSE Methods
  // ============================================================================

  /**
   * Get HKDSE texts
   * @param {number} year - Year
   * @param {string} category - Category
   * @param {number} page - Page
   * @param {number} limit - Limit
   * @returns {Promise<Object>} HKDSE texts
   */
  async getHKDSETexts(year = null, category = null, page = 1, limit = 20) {
    // For now, HKDSE texts list is still using static data
    // This method can be used when A2A backend fully supports HKDSE text listing
    if (!this.useA2A) {
      // Return empty result if A2A is disabled (static data is used in component)
      return { texts: [], pagination: { page, limit, total: 0 } };
    }

    try {
      this.log('getHKDSETexts: Using A2A backend');
      const result = await a2aChineseService.getHKDSETexts(year, category, page, limit);
      this.log('getHKDSETexts: A2A success', {
        textsCount: result.texts ? result.texts.length : 0
      });
      return result;
    } catch (error) {
      this.logError('getHKDSETexts: A2A failed', error);
      // Return empty result on error (component will use static data)
      return { texts: [], pagination: { page, limit, total: 0 } };
    }
  }

  /**
   * Check if A2A has a specific capability (with caching)
   * @param {string} capabilityName - Capability name
   * @returns {Promise<boolean>} True if capability exists
   */
  async checkCapability(capabilityName) {
    // Check cache first
    if (this.capabilityCache.has(capabilityName)) {
      return this.capabilityCache.get(capabilityName);
    }
    
    // Check A2A if enabled
    if (!this.useA2A) {
      this.capabilityCache.set(capabilityName, false);
      return false;
    }
    
    try {
      const hasCap = await a2aChineseService.hasCapability(capabilityName);
      this.capabilityCache.set(capabilityName, hasCap);
      return hasCap;
    } catch (error) {
      // If capability check fails, assume not available
      this.capabilityCache.set(capabilityName, false);
      return false;
    }
  }

  /**
   * Get HKDSE original text by ID (with Supabase fallback)
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Original text data
   */
  async getHKDSEOriginalText(textId) {
    console.log(`[ChineseDataService] getHKDSEOriginalText called with textId: ${textId}`);
    
    // Check if A2A supports this capability
    const hasCapability = await this.checkCapability('get_hkdse_original_text');
    console.log(`[ChineseDataService] getHKDSEOriginalText capability check: ${hasCapability}`);
    
    if (!hasCapability) {
      // Capability not available, use Supabase directly (no error thrown)
      console.log('[ChineseDataService] getHKDSEOriginalText: A2A capability not available, using Supabase directly');
      if (supabaseClient) {
        const { getOriginalTextById } = require('./supabase/hkdseOriginalTextService');
        return await getOriginalTextById(textId);
      }
      throw new Error('Supabase fallback not available');
    }

    return await this.tryWithFallback(
      // A2A: Get original text
      async () => {
        console.log(`[ChineseDataService] getHKDSEOriginalText: Calling A2A service with textId: ${textId}`);
        const result = await a2aChineseService.getHKDSEOriginalText(textId);
        console.log(`[ChineseDataService] getHKDSEOriginalText: A2A result:`, {
          hasResult: !!result,
          success: result?.success,
          hasData: !!result?.data
        });
        // Backend returns: { success: true, data: {...} } or { success: true, data: null }
        if (result && result.success) {
          // Return data (even if null) - component handles null
          return result.data !== undefined ? result.data : null;
        }
        // If result structure is unexpected, return null to trigger fallback
        console.warn('[ChineseDataService] getHKDSEOriginalText: Unexpected result structure, triggering fallback');
        return null;
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            console.log(`[ChineseDataService] getHKDSEOriginalText: Using Supabase fallback for textId: ${textId}`);
            const { getOriginalTextById } = require('./supabase/hkdseOriginalTextService');
            return await getOriginalTextById(textId);
          }
        : null,
      'getHKDSEOriginalText'
    );
  }

  /**
   * Get HKDSE quiz by text ID (with Supabase fallback)
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Quiz data
   */
  async getHKDSEQuiz(textId) {
    console.log(`[ChineseDataService] getHKDSEQuiz called with textId: ${textId}`);
    
    // Check if A2A supports this capability
    const hasCapability = await this.checkCapability('get_hkdse_quiz');
    console.log(`[ChineseDataService] getHKDSEQuiz capability check: ${hasCapability}`);
    
    if (!hasCapability) {
      // Capability not available, use Supabase directly (no error thrown)
      console.log('[ChineseDataService] getHKDSEQuiz: A2A capability not available, using Supabase directly');
      const { getHKDSEQuizByTextId } = require('./supabase/hkdseQuizService');
      return await getHKDSEQuizByTextId(textId);
    }

    return await this.tryWithFallback(
      // A2A: Get quiz
      async () => {
        console.log(`[ChineseDataService] getHKDSEQuiz: Calling A2A service with textId: ${textId}`);
        const result = await a2aChineseService.getHKDSEQuiz(textId);
        console.log(`[ChineseDataService] getHKDSEQuiz: A2A result:`, {
          hasResult: !!result,
          success: result?.success,
          hasQuiz: !!result?.quiz
        });
        // Backend returns: { success: true, quiz: {...} }
        if (result && result.success && result.quiz) {
          return result.quiz;
        }
        // Fallback: return result as-is if structure is different
        console.warn('[ChineseDataService] getHKDSEQuiz: Unexpected result structure');
        return result;
      },
      // Supabase fallback
      async () => {
        console.log(`[ChineseDataService] getHKDSEQuiz: Using Supabase fallback for textId: ${textId}`);
        const { getHKDSEQuizByTextId } = require('./supabase/hkdseQuizService');
        return await getHKDSEQuizByTextId(textId);
      },
      'getHKDSEQuiz'
    );
  }

  /**
   * Get HKDSE flash cards by text ID (with Supabase fallback)
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Flash cards data
   */
  async getHKDSEFlashCards(textId) {
    console.log(`[ChineseDataService] getHKDSEFlashCards called with textId: ${textId}`);
    
    // Check if A2A supports this capability
    const hasCapability = await this.checkCapability('get_hkdse_flash_cards');
    console.log(`[ChineseDataService] getHKDSEFlashCards capability check: ${hasCapability}`);
    
    if (!hasCapability) {
      // Capability not available, use Supabase directly (no error thrown)
      console.log('[ChineseDataService] getHKDSEFlashCards: A2A capability not available, using Supabase directly');
      const { getHKDSEFlashCardsByTextId } = require('./supabase/hkdseFlashCardsService');
      return await getHKDSEFlashCardsByTextId(textId);
    }

    return await this.tryWithFallback(
      // A2A: Get flash cards
      async () => {
        console.log(`[ChineseDataService] getHKDSEFlashCards: Calling A2A service with textId: ${textId}`);
        const result = await a2aChineseService.getHKDSEFlashCards(textId);
        console.log(`[ChineseDataService] getHKDSEFlashCards: A2A result:`, {
          hasResult: !!result,
          success: result?.success,
          hasFlashCards: !!result?.flashCards
        });
        // Backend returns: { success: true, flashCards: { id, textId, title, cards: [...] } }
        if (result && result.success && result.flashCards) {
          // Backend now returns the full object structure matching Supabase format
          return result.flashCards;
        }
        // If result structure is unexpected, return null to trigger fallback
        console.warn('[ChineseDataService] getHKDSEFlashCards: Unexpected result structure, triggering fallback');
        return null;
      },
      // Supabase fallback
      async () => {
        console.log(`[ChineseDataService] getHKDSEFlashCards: Using Supabase fallback for textId: ${textId}`);
        const { getHKDSEFlashCardsByTextId } = require('./supabase/hkdseFlashCardsService');
        return await getHKDSEFlashCardsByTextId(textId);
      },
      'getHKDSEFlashCards'
    );
  }

  /**
   * Get HKDSE Q&A by text ID (with Supabase fallback)
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Q&A data
   */
  async getHKDSEQA(textId) {
    console.log(`[ChineseDataService] getHKDSEQA called with textId: ${textId}`);
    
    // Check if A2A supports this capability
    const hasCapability = await this.checkCapability('get_hkdse_qa');
    console.log(`[ChineseDataService] getHKDSEQA capability check: ${hasCapability}`);
    
    if (!hasCapability) {
      // Capability not available, use Supabase directly (no error thrown)
      console.log('[ChineseDataService] getHKDSEQA: A2A capability not available, using Supabase directly');
      const { getQAByTextId } = require('./supabase/hkdseQAService');
      return await getQAByTextId(textId);
    }

    return await this.tryWithFallback(
      // A2A: Get Q&A
      async () => {
        console.log(`[ChineseDataService] getHKDSEQA: Calling A2A service with textId: ${textId}`);
        const result = await a2aChineseService.getHKDSEQA(textId);
        console.log(`[ChineseDataService] getHKDSEQA: A2A result:`, {
          hasResult: !!result,
          success: result?.success,
          hasQA: !!result?.qa
        });
        // Backend returns: { success: true, qa: { title, sections: {...} } }
        if (result && result.success && result.qa) {
          return result.qa;
        }
        // Fallback: return result as-is if structure is different
        console.warn('[ChineseDataService] getHKDSEQA: Unexpected result structure');
        return result;
      },
      // Supabase fallback
      async () => {
        console.log(`[ChineseDataService] getHKDSEQA: Using Supabase fallback for textId: ${textId}`);
        const { getQAByTextId } = require('./supabase/hkdseQAService');
        return await getQAByTextId(textId);
      },
      'getHKDSEQA'
    );
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Get feature flags status
   * @returns {Object} Feature flags
   */
  getFeatureFlags() {
    return {
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase,
      enableLogging: this.enableLogging
    };
  }

  /**
   * Set feature flag
   * @param {string} flag - Flag name
   * @param {boolean} value - Flag value
   */
  setFeatureFlag(flag, value) {
    if (flag === 'useA2A') {
      this.useA2A = value;
      this.log(`Feature flag updated: useA2A = ${value}`);
    } else if (flag === 'fallbackToSupabase') {
      this.fallbackToSupabase = value;
      this.log(`Feature flag updated: fallbackToSupabase = ${value}`);
    } else if (flag === 'enableLogging') {
      this.enableLogging = value;
    }
  }

  /**
   * Get service status
   * @returns {Object} Service status
   */
  async getServiceStatus() {
    const status = {
      a2a: {
        enabled: this.useA2A,
        available: false,
        capabilities: 0
      },
      supabase: {
        fallbackEnabled: this.fallbackToSupabase,
        available: supabaseClient !== null
      }
    };

    if (this.useA2A) {
      try {
        const capabilities = a2aChineseService.getCapabilities();
        if (capabilities) {
          status.a2a.available = true;
          status.a2a.capabilities = capabilities.length;
        }
      } catch (error) {
        this.logError('Failed to get A2A status', error);
      }
    }

    return status;
  }

  /**
   * Cleanup - call when unmounting app
   */
  cleanup() {
    this.log('Cleaning up ChineseDataService');
    a2aChineseService.cleanup();
  }
}

/**
 * Create and export singleton instance
 */
const chineseDataService = new ChineseDataService();

export default chineseDataService;

/**
 * Export class for testing or custom instances
 */
export { ChineseDataService, FeatureFlags };

